import { useEffect, useMemo, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { AvailableModules } from 'generated/featureDefinitions'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useCreateUpsellRequest, { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { useSelfUpsellContext } from 'modules/upsell/store/SelfUpsellProvider'
import { readLatestUpsellOrigin } from 'modules/upselling_kit/utils/localStorage'

import BaseModal from './BaseModal'
import ErrorStep from './ErrorStep'
import FirstStep from './FirstStep'
import SecondStep from './SecondStep'
import SuccessStep from './SuccessStep'
import SurveyStep from './SurveyStep'

type CheckoutModalProps = {
  isOpen: boolean
  onClose: () => void
  moduleName: AvailableModules
  moduleSlug: string
  from: string
  installedAddonInternalId?: string | null
  hubspotModuleName?: string | null
  moduleTranslatedName?: string
}

const CheckoutModal = ({
  isOpen,
  onClose,
  moduleName,
  moduleSlug,
  from,
  installedAddonInternalId,
  hubspotModuleName,
  moduleTranslatedName,
}: CheckoutModalProps) => {
  const [step, setStep] = useState('first')
  const [shouldReopenModal, setShouldReopenModal] = useState<boolean>(false)
  const {
    allAddons,
    shouldBypassSelfUpsell,
    togglePlanSelect,
    allCrossSellingAddons,
    allMandatoryCrossSellingAddons,
  } = useSelfUpsellContext()
  const addons = allAddons.f25Addons
  const [selectedAddonId, setSelectedAddonId] = useState(addons[0]?.internalTitle || '')
  useEffect(() => {
    if (!addons.length) return

    setSelectedAddonId((current) => {
      if (current && addons.some((addon) => addon.internalTitle === current)) {
        return current
      }

      return addons[0]?.internalTitle || ''
    })
  }, [addons])

  const addon = useMemo(() => {
    return addons.find((a) => a.internalTitle === selectedAddonId) || addons[0]
  }, [addons, selectedAddonId])

  const handleSelectAddon = (selectedAddon: (typeof addons)[number]) => {
    if (!selectedAddon.isSelected) togglePlanSelect(selectedAddon)
  }

  const { create } = useCreateUpsellRequest()

  const handleRequestUpsell = () => {
    if (shouldBypassSelfUpsell) return
    const latestUpsellOrigin = readLatestUpsellOrigin()
    const upsellOrigin = latestUpsellOrigin?.request_source
    const requestSourceCategory = latestUpsellOrigin?.request_source_category || 'Organic'
    const requestSourceComponent = latestUpsellOrigin?.request_source_component

    const abandonedCartRequestSource = upsellOrigin || 'Marketplace'
    create({
      requestSource: abandonedCartRequestSource,
      requestSourceCategory,
      requestSourceComponent,
      moduleRequested: moduleName,
      requestReason: ValidReasons.MissingAddon,
      upsellRequestStatus: 'Opportunity detected',
      upsellRequestComponentType: 'self_upsell_modal',
      upsellRequestType: 'Abandoned cart',
      hubspotModuleName: hubspotModuleName || undefined,
    })
  }

  const handleClose = () => {
    setShouldReopenModal(false)
    track(trackingEvents.upsell.closeSelfUpsellModal, {
      module: moduleName,
      step: step,
    })
    if (step === 'success') {
      onClose()
      window.location.reload()
      return
    }
    if (step !== 'success' && !shouldBypassSelfUpsell) {
      handleRequestUpsell()
      onClose()
      setStep('survey')
    } else {
      onClose()
      setStep('first')
    }
  }

  const handleAssistance = () => {
    track(trackingEvents.upsell.clickRequestAssistanceButton, {
      module: moduleName,
      step: step,
    })
    app.setFlash({
      type: 'correct',
      message: i18n.t('upselling_kit.upselling_button.success_message.title'),
    })
    handleClose()
  }

  useEffect(() => {
    if (isOpen) {
      track(trackingEvents.upsell.viewSelfUpsellModal, {
        module: moduleName,
        from,
      })
    }
  }, [isOpen, moduleName, from])

  if (!addon) return null

  return (
    <>
      <BaseModal isOpen={isOpen || shouldReopenModal} onClose={handleClose} width="760px" isLocked>
        <Box
          style={{
            minHeight: '560px',
            maxHeight: '90vh',
            overflowY: 'auto',
          }}
        >
          {step === 'first' && (
            <FirstStep
              addon={addon}
              step={step}
              setStep={setStep}
              setSelectedAddonId={setSelectedAddonId}
              installedAddonInternalId={installedAddonInternalId}
              crossSellingAddons={allCrossSellingAddons.f25Addons}
              availablePlans={addons}
              onSelectAddon={handleSelectAddon}
              handleAssistance={handleAssistance}
              moduleTranslatedName={moduleTranslatedName}
              moduleName={moduleName}
              moduleSlug={moduleSlug}
            />
          )}
          {step === 'second' && (
            <SecondStep
              addon={addon}
              crossSellingAddons={allCrossSellingAddons.f25Addons}
              mandatoryCrossSellingAddons={allMandatoryCrossSellingAddons.f25Addons}
              step={step}
              setStep={setStep}
              moduleName={moduleName}
            />
          )}
          {step === 'success' && (
            <SuccessStep addon={addon} step={step} onClose={handleClose} moduleName={moduleName} />
          )}
          {step === 'error' && (
            <ErrorStep
              addon={addon}
              step={step}
              onClose={handleClose}
              handleAssistance={handleAssistance}
              moduleName={moduleName}
            />
          )}
        </Box>
      </BaseModal>

      {step === 'survey' && (
        <SurveyStep
          moduleName={moduleName}
          moduleSlug={moduleSlug}
          reopenModal={() => {
            setShouldReopenModal(true)
            setStep('first')
          }}
          onClose={() => {
            setStep('first')
            onClose()
          }}
        />
      )}
    </>
  )
}

export default CheckoutModal
